// The clinic tab strip every page below the masthead uses.
//
// Ground truth: the deployed board, opened and screenshotted 2026-08-15. Tasks,
// Money, People and Payroll all work the same way — you pick ONE clinic from a
// strip of uppercase letterspaced tabs with a purple underline on the active
// one, and the page below shows that clinic's things.
//
// Two rules ride in here so no page can get them wrong on its own:
//   1. canonical clinic order (INV-14), from the one source, never per-page;
//   2. the strip lists the VIEWER's clinics. The owner and the director see
//      four; a manager has one and gets NO strip at all — a lone tab is a
//      control that does nothing (owner, 2026-08-15). Server-side scoping is
//      unchanged and still the thing that enforces access (INV-3); this only
//      decides what is worth drawing.
import type { JSX } from 'preact';
import { session } from '../lib/session';
import { orderLocations, LOCATION_ORDER, LOCATION_NAMES, type LocationCode } from '../lib/locations';

/** The clinics this viewer can see, canonical order. Falls back to all four
 * when there is no session yet — a page rendered without one (a test, a cold
 * first paint) shows the full set rather than nothing. */
export function visibleClinics(): LocationCode[] {
  const mine = orderLocations((session.value.me?.locations ?? []) as LocationCode[]);
  return mine.length ? mine : orderLocations([...LOCATION_ORDER] as LocationCode[]);
}

/** Resolve the active clinic: the chosen one if it is still visible, else the
 * first. Pages hold the selection in their own state; this keeps the
 * "is it still valid" logic in one place. */
export function activeClinicFrom(chosen: string | null, clinics: LocationCode[]): LocationCode {
  return chosen && clinics.includes(chosen as LocationCode) ? (chosen as LocationCode) : clinics[0];
}

export const MANAGEMENT_TAB_ID = 'MANAGEMENT';
export const EXPECTED_REPORTS_TAB_ID = 'EXPECTED_REPORTS';

export function canSeeManagementTab(
  me: { role?: string | null; viewingAs?: boolean } | null | undefined = session.value.me,
): boolean {
  // View-as is "show me what a manager sees". A manager never gets Management,
  // so the owner lens must not keep the tab or the folder.
  if (!me || me.viewingAs) return false;
  return me.role === 'OWNER' || me.role === 'DIRECTOR';
}

/**
 * `trailing` renders at the RIGHT-HAND END of the clinic row — owner,
 * 2026-08-15: "the search box should be at the right of the clinic names."
 * It sits OUTSIDE the `role="tablist"` element on purpose: a search input
 * announced as a tab inside a tab list is wrong for anyone using a screen
 * reader, however right it looks.
 *
 * A manager has one clinic and gets no tabs (a lone tab is a control that does
 * nothing), but they still need the search box — so the row is still rendered
 * when there is trailing content, just with nothing on its left.
 */
export function ClinicTabs(
  { clinics, active, onSelect, trailing, after, onSelectAfter, management = true }:
  {
    clinics: LocationCode[];
    active: string;
    onSelect: (code: LocationCode) => void;
    trailing?: JSX.Element | null;
    after?: { id: string; label: string }[];
    onSelectAfter?: (id: string) => void;
    /** Owner, 2026-09-10: "remove management link from Payroll dashboard" —
     *  a page passes false to keep the clinic tabs without the Management tab. */
    management?: boolean;
  },
): JSX.Element | null {
  const afterTabs = [...(after ?? [])];
  if (
    management
    && canSeeManagementTab()
    && !afterTabs.some((tab) => tab.id === MANAGEMENT_TAB_ID)
  ) {
    afterTabs.push({ id: MANAGEMENT_TAB_ID, label: 'Management' });
  }
  const showClinicTabs = clinics.length >= 2;
  const showAfter = afterTabs.length > 0;
  // One clinic plus an extra tab still needs both buttons — otherwise the
  // extra tab is a one-way door with no way back to the clinic.
  const showLoneClinic = !showClinicTabs && showAfter && clinics.length === 1;
  const showTablist = showClinicTabs || showAfter;
  if (!showTablist && !trailing) return null;
  return (
    <div class="clinic-row">
      {showTablist ? (
        <div class="clinic-tabs" role="tablist" aria-label="Clinic">
          {(showClinicTabs || showLoneClinic) ? clinics.map((code) => (
            <button
              key={code}
              type="button"
              role="tab"
              aria-selected={code === active}
              class={code === active ? 'tab active' : 'tab'}
              onClick={() => onSelect(code)}
            >
              {LOCATION_NAMES[code] ?? code}
            </button>
          )) : null}
          {afterTabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={tab.id === active}
              class={tab.id === active ? 'tab active' : 'tab'}
              onClick={() => onSelectAfter?.(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>
      ) : <div class="clinic-tabs" />}
      <div class="clinic-row-trailing">{trailing}</div>
    </div>
  );
}
